/**
 * PDF Viewer Zoom Configuration
 * Implements Material Design 3 and International UX Standards
 * 
 * Standards Compliance:
 * - PDF/UA (Universal Accessibility) - 10-400% zoom range
 * - WCAG 2.1 Level AA - 1.4.4 (Text Resize up to 200%)
 * - ISO 9241-143 (Forms user guidance) - Predictable zoom behavior
 * - Material Design 3 - Motion and interaction patterns
 */

import { MD3_MOTION } from '../components/MaterialDesignConstants';

export const ZOOM_CONFIG = {
  // Zoom Range (PDF/UA compliant)
  MIN_ZOOM: 10,
  MAX_ZOOM: 400,
  DEFAULT_ZOOM: 100,
  
  // Zoom Increments (Fine control with smart stops)
  ZOOM_INCREMENT: 10, // 10% steps for precise control
  ZOOM_INCREMENT_COARSE: 25, // 25% for button clicks
  
  // Smart Preset Levels (Common zoom levels)
  PRESET_LEVELS: [10, 25, 50, 75, 100, 125, 150, 175, 200, 250, 300, 400] as const,
  
  // Zoom Behavior
  ZOOM_ANCHOR: 'viewport-center' as const, // Center of visible content area
  MAINTAIN_CENTER_LOCK: true, // Keep center point fixed during zoom
  
  // Animation (Material Design 3 Standard)
  TRANSITION_DURATION: 200, // ms - MD3 simple transition
  TRANSITION_EASING: MD3_MOTION.EASING.STANDARD, // cubic-bezier(0.4, 0.0, 0.2, 1)
  RESPECT_REDUCED_MOTION: true, // Disable animation if user prefers
  
  // Input Methods
  SUPPORTED_INPUTS: {
    BUTTONS: true,
    SLIDER: true,
    MOUSE_WHEEL: true,
    PINCH_GESTURE: true,
    KEYBOARD: true,
    DOUBLE_CLICK: true,
  },
  
  // Mouse Wheel
  MOUSE_WHEEL_ZOOM_DELTA: 10, // 10% per scroll notch
  MOUSE_WHEEL_DEBOUNCE: 50, // ms - Debounce rapid scrolling
  
  // Keyboard Shortcuts
  KEYBOARD_SHORTCUTS: {
    ZOOM_IN: ['+', '='],
    ZOOM_OUT: ['-', '_'],
    FIT_WIDTH: 'w',
    FIT_PAGE: 'p',
    ACTUAL_SIZE: '1',
    FIT_SCREEN: 'f',
  },
  
  // Feedback
  SHOW_PERSISTENT_ZOOM: true, // Show zoom % in toolbar
  SHOW_TRANSIENT_TOAST: true, // Show toast on zoom change
  TOAST_DURATION: 2000, // ms - 2 seconds
  TOAST_POSITION: {
    vertical: 'bottom' as const,
    horizontal: 'center' as const,
  },
  
  // Accessibility (WCAG 2.1 AA)
  ANNOUNCE_ZOOM_LEVEL: true, // Screen reader announcements
  MIN_TOUCH_TARGET_SIZE: 44, // px - WCAG 2.5.5
  
  // Performance
  DEBOUNCE_RAPID_ZOOM: true, // Prevent animation stacking
  DEBOUNCE_DURATION: 150, // ms
  
  // Preset Actions
  PRESETS: {
    FIT_WIDTH: {
      name: 'Fit Width',
      shortcut: 'W',
      calculate: 'fitWidth' as const,
    },
    FIT_PAGE: {
      name: 'Fit Page',
      shortcut: 'P',
      calculate: 'fitPage' as const,
    },
    ACTUAL_SIZE: {
      name: 'Actual Size',
      shortcut: '1',
      value: 100,
    },
    FILL_SCREEN: {
      name: 'Fill Screen',
      shortcut: 'F',
      calculate: 'fillScreen' as const,
    },
  },
} as const;

/**
 * Calculate zoom level to fit width
 */
export function calculateFitWidthZoom(
  containerWidth: number,
  contentWidth: number,
  padding: number = 32
): number {
  const availableWidth = containerWidth - padding;
  const zoom = (availableWidth / contentWidth) * 100;
  return clampZoom(Math.floor(zoom));
}

/**
 * Calculate zoom level to fit entire page
 */
export function calculateFitPageZoom(
  containerWidth: number,
  containerHeight: number,
  contentWidth: number,
  contentHeight: number,
  padding: number = 32
): number {
  const availableWidth = containerWidth - padding;
  const availableHeight = containerHeight - padding;
  
  const widthRatio = availableWidth / contentWidth;
  const heightRatio = availableHeight / contentHeight;
  
  // Use smaller ratio to fit completely
  const zoom = Math.min(widthRatio, heightRatio) * 100;
  return clampZoom(Math.floor(zoom));
}

/**
 * Calculate zoom level to fill screen (maximize content)
 */
export function calculateFillScreenZoom(
  containerWidth: number,
  containerHeight: number,
  contentWidth: number,
  contentHeight: number,
  padding: number = 32
): number {
  const availableWidth = containerWidth - padding;
  const availableHeight = containerHeight - padding;
  
  const widthRatio = availableWidth / contentWidth;
  const heightRatio = availableHeight / contentHeight;
  
  // Use larger ratio to fill screen (may crop)
  const zoom = Math.max(widthRatio, heightRatio) * 100;
  return clampZoom(Math.floor(zoom));
}

/**
 * Clamp zoom value to valid range
 */
export function clampZoom(zoom: number): number {
  return Math.max(ZOOM_CONFIG.MIN_ZOOM, Math.min(ZOOM_CONFIG.MAX_ZOOM, zoom));
}

/**
 * Get nearest smart preset zoom level
 */
export function getNearestPresetZoom(currentZoom: number): number {
  const presets = ZOOM_CONFIG.PRESET_LEVELS;
  return presets.reduce((prev, curr) => 
    Math.abs(curr - currentZoom) < Math.abs(prev - currentZoom) ? curr : prev
  );
}

/**
 * Check if user prefers reduced motion
 */
export function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Get transition duration based on user preference
 */
export function getTransitionDuration(): number {
  if (ZOOM_CONFIG.RESPECT_REDUCED_MOTION && prefersReducedMotion()) {
    return 0; // No animation
  }
  return ZOOM_CONFIG.TRANSITION_DURATION;
}

/**
 * Calculate viewport center point
 * Returns the center point of the visible viewport area
 */
export function calculateViewportCenter(
  container: HTMLElement
): { x: number; y: number } {
  const rect = container.getBoundingClientRect();
  return {
    x: rect.width / 2,
    y: rect.height / 2,
  };
}

/**
 * Calculate content center point in content coordinates
 * This is the point in the content that's currently at the viewport center
 */
export function calculateContentCenterPoint(
  container: HTMLElement,
  currentZoom: number
): { x: number; y: number } {
  const scale = currentZoom / 100;
  const viewportCenter = calculateViewportCenter(container);
  
  return {
    x: (container.scrollLeft + viewportCenter.x) / scale,
    y: (container.scrollTop + viewportCenter.y) / scale,
  };
}

/**
 * Calculate scroll adjustment to maintain center point after zoom
 * This ensures the same content point stays at the viewport center
 */
export function calculateScrollAdjustment(
  container: HTMLElement,
  contentCenter: { x: number; y: number },
  newZoom: number
): { scrollLeft: number; scrollTop: number } {
  const newScale = newZoom / 100;
  const viewportCenter = calculateViewportCenter(container);
  
  return {
    scrollLeft: contentCenter.x * newScale - viewportCenter.x,
    scrollTop: contentCenter.y * newScale - viewportCenter.y,
  };
}

/**
 * Apply zoom with center-point anchoring
 * This is the main function to zoom while keeping the viewport center fixed
 */
export function applyZoomWithCenterAnchor(
  container: HTMLElement,
  currentZoom: number,
  newZoom: number,
  animated: boolean = true
): void {
  // Calculate content point currently at viewport center
  const contentCenter = calculateContentCenterPoint(container, currentZoom);
  
  // Calculate new scroll position to maintain center point
  const newScroll = calculateScrollAdjustment(container, contentCenter, newZoom);
  
  // Apply scroll adjustment
  // Use smooth scrolling if animated and user doesn't prefer reduced motion
  const behavior = animated && !prefersReducedMotion() ? 'smooth' : 'auto';
  
  container.scrollTo({
    left: Math.max(0, newScroll.scrollLeft),
    top: Math.max(0, newScroll.scrollTop),
    behavior,
  });
}

/**
 * Calculate zoom from cursor position (for mouse wheel zoom)
 */
export function calculateCursorBasedZoom(
  container: HTMLElement,
  cursorX: number,
  cursorY: number,
  currentZoom: number,
  newZoom: number
): { scrollLeft: number; scrollTop: number } {
  const currentScale = currentZoom / 100;
  const newScale = newZoom / 100;
  
  const rect = container.getBoundingClientRect();
  
  // Get cursor position relative to container
  const relativeX = cursorX - rect.left;
  const relativeY = cursorY - rect.top;
  
  // Calculate content point under cursor
  const contentX = (container.scrollLeft + relativeX) / currentScale;
  const contentY = (container.scrollTop + relativeY) / currentScale;
  
  // Calculate new scroll to keep cursor over same content point
  return {
    scrollLeft: contentX * newScale - relativeX,
    scrollTop: contentY * newScale - relativeY,
  };
}